import React, { useRef, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import {
    Volume2, Lightbulb, Package, Settings2, Monitor, Layers,
    Cpu, Wrench, Star, Zap, ArrowUpRight,
} from "lucide-react";
import { api } from "../../services/api";

const ICON_MAP = { Volume2, Lightbulb, Package, Settings2, Monitor, Layers, Cpu, Wrench, Star, Zap };

function TiltCard({ children, className, isCyan, accentColor, ...props }) {
    const ref = useRef(null);
    const xRaw = useMotionValue(0);
    const yRaw = useMotionValue(0);

    const springCfg = { stiffness: 280, damping: 28 };
    const rotateX = useSpring(useTransform(yRaw, [-0.5, 0.5], [8, -8]), springCfg);
    const rotateY = useSpring(useTransform(xRaw, [-0.5, 0.5], [-8, 8]), springCfg);
    const glareX = useSpring(useTransform(xRaw, [-0.5, 0.5], ["-80%", "80%"]), springCfg);
    const glareY = useSpring(useTransform(yRaw, [-0.5, 0.5], ["-80%", "80%"]), springCfg);

    const handleMouseMove = (e) => {
        const rect = ref.current.getBoundingClientRect();
        xRaw.set((e.clientX - rect.left) / rect.width - 0.5);
        yRaw.set((e.clientY - rect.top) / rect.height - 0.5);
    };

    const handleMouseLeave = () => {
        xRaw.set(0);
        yRaw.set(0);
    };

    return (
        <motion.div
            ref={ref}
            style={{ rotateX, rotateY, transformPerspective: 900 }}
            onMouseMove={handleMouseMove}
            onMouseLeave={handleMouseLeave}
            className={className}
            {...props}
        >
            {/* Glare highlight */}
            <motion.div
                style={{
                    x: glareX,
                    y: glareY,
                    background: `radial-gradient(circle at center, ${accentColor ? accentColor + "1a" : isCyan ? "rgba(0,153,255,0.1)" : "rgba(0,85,204,0.1)"} 0%, transparent 55%)`,
                }}
                className="absolute w-[200%] h-[200%] -top-1/2 -left-1/2 pointer-events-none rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300"
            />
            {children}
        </motion.div>
    );
}

export default function Services() {
    const [services, setServices] = useState([]);

    useEffect(() => {
        api.getSluzby().then(setServices).catch(() => {});
    }, []);

    return (
        <section
            id="services"
            className="relative py-24 sm:py-32 px-6 sm:px-12 bg-nova-black overflow-hidden"
            data-testid="services-section"
        >
            <div className="absolute inset-0 grid-bg opacity-40 pointer-events-none" />
            <div className="absolute top-40 right-0 w-[500px] h-[500px] bg-nova-purple/10 rounded-full blur-[140px] pointer-events-none" />

            <div className="relative max-w-7xl mx-auto">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-100px" }}
                    transition={{ duration: 0.6 }}
                    className="max-w-3xl mb-16"
                >
                    <div className="font-mono text-xs uppercase tracking-[0.25em] text-nova-cyan mb-4">
                        / 01 — Služby
                    </div>
                    <h2 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter leading-[1.05]">
                        Všetko, čo váš event{" "}
                        <span className="text-nova-cyan">technicky</span>{" "}
                        potrebuje
                    </h2>
                    <p className="mt-6 text-lg text-zinc-400 max-w-2xl">
                        Jeden tím, jedna zodpovednosť. Dodávame komplexné
                        technické riešenia bez kompromisov.
                    </p>
                </motion.div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {services.map((s, i) => {
                        const Icon = ICON_MAP[s.icon_name] || Layers;
                        const desc = s.short_desc || s.desc || "";
                        return (
                            <motion.div
                                key={s.slug}
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: "-60px" }}
                                transition={{ duration: 0.6, delay: i * 0.08 }}
                                data-testid={`service-card-${i}`}
                                style={{ transformStyle: "preserve-3d" }}
                            >
                                <Link to={`/sluzby/${s.slug}`} className="block h-full no-underline">
                                <TiltCard
                                    isCyan={true}
                                    accentColor={s.accent}
                                    className="group relative p-8 bg-nova-surface border border-white/8 rounded-xl hover:border-white/20 transition-colors duration-400 overflow-hidden cursor-pointer h-full"
                                >
                                    {/* Corner glow */}
                                    <div
                                        className="absolute -top-20 -right-20 w-56 h-56 rounded-full blur-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-700"
                                        style={{ background: `${s.accent}40` }}
                                    />

                                    <div className="relative">
                                        <div className="w-12 h-12 rounded-md flex items-center justify-center mb-6 border transition-colors duration-300"
                                            style={{
                                                background: s.faint || `${s.accent}14`,
                                                borderColor: s.border || `${s.accent}33`,
                                                color: s.accent,
                                            }}>
                                            <Icon className="w-5 h-5" strokeWidth={1.6} />
                                        </div>

                                        <h3 className="font-heading text-xl font-bold text-white mb-3">
                                            {s.title}
                                        </h3>
                                        <p className="text-sm text-zinc-400 leading-relaxed">
                                            {desc}
                                        </p>

                                        <div className="mt-6 flex items-center gap-2 text-xs font-mono uppercase tracking-wider text-zinc-600 group-hover:text-white transition-colors">
                                            Zistiť viac
                                            <ArrowUpRight className="w-4 h-4 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
                                        </div>
                                    </div>
                                </TiltCard>
                                </Link>
                            </motion.div>
                        );
                    })}
                </div>
            </div>
        </section>
    );
}
